import React, { useEffect, useState } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import Dialog from "common/basicComponents/Dialog";
import Typography, {
  TypographyVariant,
  TypographyWeight,
} from "common/basicComponents/Typography";
import Button, {
  ButtonVariant,
  ButtonType,
} from "common/basicComponents/Button";
import Box from "common/basicComponents/Box";
import LabelledCheckbox, {
  CheckboxType,
} from "common/basicComponents/LabelledCheckbox";
import { useViewport, ViewportType } from "utils/ViewportContext";
import { formatUnixDMY } from "utils/DateTimeUtils";

const Buttons = styled.div`
  display: flex;
  flex-direction: ${({ viewport }) =>
    viewport > ViewportType.MOBILE ? "row" : "column-reverse"};
  margin-top: 2.5rem;
  width: 100%;
`;

const CancelSubscriptionDialog = ({ open, onClose, onConfirm, periodEnd }) => {
  const { t } = useTranslation("billing");
  const { type: viewportType } = useViewport();
  const [immediate, setImmediate] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setImmediate(false);
  }, [open]);

  const confirm = async () => {
    setSubmitting(true);
    await onConfirm(immediate);
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <Box padding="0 0 1.5rem">
        <Typography
          variant={TypographyVariant.H4}
          weight={TypographyWeight.BOLD}
          text={t("cancelSubscriptionTitle")}
        />
      </Box>
      <Box padding="0 0 1rem">
        <Typography
          variant={TypographyVariant.BODY1}
          text={
            immediate
              ? t("cancelImmediatelyDescription")
              : t("cancelAtPeriodEndDescription", {
                  date: formatUnixDMY(periodEnd),
                })
          }
        />
      </Box>
      <LabelledCheckbox
        id="cancelImmediately"
        type={CheckboxType.CONTAINED}
        label={t("cancelImmediately")}
        value={immediate}
        onChange={setImmediate}
      />
      <Buttons viewport={viewportType}>
        <Box
          width={viewportType < ViewportType.TABLET ? "100%" : "50%"}
          padding={
            viewportType < ViewportType.TABLET ? "0.625rem 0" : "0 1rem 0 0"
          }
        >
          <Button
            variant={ButtonVariant.OUTLINED}
            text={t("keepSubscription")}
            minWidth="13rem"
            width="100%"
            onClick={onClose}
            disabled={submitting}
          />
        </Box>
        <Box
          width={viewportType < ViewportType.TABLET ? "100%" : "50%"}
          padding={
            viewportType < ViewportType.TABLET ? "0.625rem 0" : "0 0 0 1rem"
          }
        >
          <Button
            variant={ButtonVariant.ERROR}
            text={t("cancelSubscription")}
            minWidth="13rem"
            width="100%"
            type={ButtonType.BUTTON}
            onClick={confirm}
            isLoading={submitting}
          />
        </Box>
      </Buttons>
    </Dialog>
  );
};

CancelSubscriptionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  periodEnd: PropTypes.number,
};

CancelSubscriptionDialog.defaultProps = {
  periodEnd: null,
};

export default CancelSubscriptionDialog;
